import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { downloadPdfWithProgress } from "@/lib/pdfDownload";
import { toast } from "sonner";
import { ArrowLeft, Loader2, TrendingUp, TrendingDown, AlertTriangle, MessageSquare, BookOpen, ArrowRight, Sparkles, Download } from "lucide-react";
import { renderRich } from "@/lib/renderRich";

const NIVEL_COLORS = ["#94a3b8", "#dc2626", "#f59e0b", "#0891b2", "#0F6E56"];
const NIVEL_BG = ["#f1f5f9", "#fee2e2", "#fef3c7", "#e0f2fe", "#d1fae5"];

// Paleta hasta 5 series (deep green + gold + accents)
const SERIES_COLORS = ["#0F6E56", "#C9A84C", "#0891b2", "#dc2626", "#7c3aed"];

/**
 * Compara 2-5 consultorías del usuario (query param `?ids=id1,id2,...`).
 * Muestra: radar comparativo, tabla por área, timeline SVG cronológico,
 * storyline narrativo, top_changes y diff clásico.
 */
export default function ConsultingCompare() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const idsParam = searchParams.get("ids") || "";
  const ids = useMemo(() => idsParam.split(",").filter(Boolean).slice(0, 5), [idsParam]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    async function run() {
      if (ids.length < 2) {
        setError("Se necesitan al menos 2 consultorías para comparar.");
        setLoading(false);
        return;
      }
      try {
        const r = await api.post("/consulting/compare", { ids });
        setData(r.data);
      } catch (err) {
        const msg = err?.response?.data?.detail || "No pudimos generar la comparativa";
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    }
    run();
  }, [idsParam]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500" data-testid="consulting-compare-loading">
        <Loader2 className="animate-spin mr-2" /> Preparando el análisis comparativo…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-rose-500 mb-3" />
          <p className="text-slate-700 mb-4">{error || "Sin datos"}</p>
          <button onClick={() => navigate("/consulting")} className="rounded-full px-5 py-2 text-sm font-semibold text-white" style={{ background: "#0F6E56" }}>
            Volver a mis consultorías
          </button>
        </div>
      </div>
    );
  }

  const { summaries, chrono, diff, storyline, top_changes, timeline_points, areas } = data;
  const n = summaries.length;

  async function handleDownloadPdf() {
    setDownloading(true);
    try {
      await downloadPdfWithProgress({
        url: `/consulting/compare/pdf?ids=${ids.join(",")}`,
        filename: `Comparativa_${n}_consultorias.pdf`,
        kind: "std",
        label: "Preparando la comparativa",
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        toast.error("Solo puedes descargar comparativas entre 2 y 5 diagnósticos.");
      } else if (err?.code === "ECONNABORTED" || err?.message?.includes("timeout")) {
        toast.error("La generación tardó demasiado. Vuelve a intentarlo en unos segundos.");
      } else {
        toast.error("No pudimos generar el PDF");
      }
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 md:px-6" data-testid="consulting-compare">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <button onClick={() => navigate("/consulting")} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="consulting-compare-back">
            <ArrowLeft className="w-4 h-4" /> Volver a mis consultorías
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={downloading}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white transition disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)", boxShadow: "0 10px 24px -12px rgba(11,61,46,0.4)" }}
            data-testid="consulting-compare-download-pdf"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {downloading ? "Generando…" : "Descargar comparativa PDF"}
          </button>
        </div>

        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest mb-1" style={{ color: "#0F6E56" }}>Comparativa de consultorías</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Evolución del talento en tu empresa</h1>
          <p className="text-slate-500 text-sm mt-1">Análisis diferencial entre {n} consultoría{n === 1 ? "" : "s"}.</p>
        </div>

        {/* Storyline narrativo */}
        {(storyline?.storyline || storyline?.titulo) && (
          <div
            className="relative rounded-3xl p-6 md:p-8 mb-6 overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
              boxShadow: "0 30px 60px -30px rgba(11,61,46,0.55), 0 0 0 1px rgba(201,168,76,0.15) inset",
            }}
            data-testid="consulting-compare-storyline"
          >
            {/* Orb decorativo esquina superior derecha (mismo que el hero del informe) */}
            <span
              aria-hidden
              className="absolute -top-8 -right-8 pointer-events-none"
              style={{
                width: 220,
                height: 220,
                background:
                  "radial-gradient(circle at 100% 0%, rgba(201,168,76,0.35) 0%, rgba(201,168,76,0) 65%)," +
                  "repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 10px)",
              }}
            />
            {/* Barra dorada inferior */}
            <span
              aria-hidden
              className="absolute bottom-0 left-0 right-0 h-1"
              style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold mb-3 px-3 py-1 rounded-full" style={{ background: "rgba(201,168,76,0.15)", color: "#E5C874" }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
                <BookOpen className="w-3.5 h-3.5" /> Trazo narrativo
              </div>
              {storyline.titulo && (
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight mb-4" style={{ letterSpacing: "-0.02em" }}>
                  {renderRich(storyline.titulo)}
                </h2>
              )}
              {storyline.storyline && (
                <div className="space-y-4 max-w-3xl">
                  {splitIntoParagraphs(storyline.storyline).map((para, i) => (
                    <p key={i} className="text-white/90 leading-relaxed text-[15px] md:text-base">
                      {renderRich(para)}
                    </p>
                  ))}
                </div>
              )}
              {storyline.highlight && (
                <div className="mt-5 inline-flex items-start gap-2 rounded-full px-4 py-2" style={{ background: "rgba(201,168,76,0.18)", border: "1px solid rgba(201,168,76,0.4)" }}>
                  <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#E5C874" }} />
                  <span className="text-sm text-white/95 font-medium">{renderRich(storyline.highlight)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timeline SVG cronológico */}
        {timeline_points && timeline_points.length >= 2 && (
          <div className="bg-white rounded-2xl shadow p-5 md:p-6 mb-6" data-testid="consulting-compare-timeline">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Línea de tiempo · índice de madurez</div>
            <TimelineSVG points={timeline_points} />
            <div className="mt-3 text-[11px] text-slate-500">
              Eje vertical: índice de madurez (0 = todo Crítico · 100 = todo Sólido). Puntos ordenados cronológicamente.
            </div>
          </div>
        )}

        {/* Top Changes */}
        {top_changes && (top_changes.positivas?.length > 0 || top_changes.negativas?.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="consulting-compare-top-changes">
            <TopChangesCard
              title="Áreas de mayor mejora"
              rows={top_changes.positivas || []}
              icon={<TrendingUp className="w-4 h-4" />}
              color="#0F6E56"
              bg="#ecfdf5"
              testid="consulting-compare-top-pos"
            />
            <TopChangesCard
              title="Áreas de mayor deterioro"
              rows={top_changes.negativas || []}
              icon={<TrendingDown className="w-4 h-4" />}
              color="#b91c1c"
              bg="#fef2f2"
              testid="consulting-compare-top-neg"
            />
          </div>
        )}

        {/* Radar comparativo (hasta 5 series) */}
        <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6 mb-6" data-testid="consulting-compare-radar">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Mapa de madurez por área</div>
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <RadarSVG summaries={summaries} areas={areas} colors={SERIES_COLORS} />
            </div>
            <div className="flex-1 w-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {summaries.map((s, idx) => (
                  <div key={s.id} className="flex items-center gap-3 min-w-0">
                    <div className="w-3.5 h-3.5 rounded flex-shrink-0" style={{ background: SERIES_COLORS[idx] }} />
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate text-sm">{s.company_name}</div>
                      <div className="text-xs text-slate-500">{s.ready_at ? new Date(s.ready_at).toLocaleDateString("es-ES") : "—"}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-xs text-slate-500 border-t pt-3">
                Escala: 1 Crítico · 2 En desarrollo · 3 Funcional · 4 Sólido
              </div>
            </div>
          </div>
        </div>

        {/* Tabla lado a lado (hasta 5 columnas) */}
        <div className="bg-white rounded-2xl shadow p-5 md:p-6 mb-6 overflow-x-auto">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Madurez por área</div>
          <table className="w-full text-sm min-w-[560px]" data-testid="consulting-compare-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
                <th className="py-2 pr-4 sticky left-0 bg-white z-10">Área</th>
                {summaries.map((s, idx) => (
                  <th key={s.id} className="py-2 px-3 text-center min-w-[120px]">
                    <div className="flex items-center justify-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded" style={{ background: SERIES_COLORS[idx] }} />
                      <span className="truncate max-w-[140px]" title={s.company_name}>{s.company_name}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {areas.map((a) => (
                <tr key={a.code} className="border-b last:border-b-0">
                  <td className="py-3 pr-4 sticky left-0 bg-white z-10">
                    <div className="font-semibold text-slate-900">{a.code}</div>
                    <div className="text-xs text-slate-500">{a.name}</div>
                  </td>
                  {summaries.map((s) => {
                    const cell = s.per_area?.[a.code] || { nivel: 0, label: "—" };
                    return (
                      <td key={s.id} className="py-3 px-3 text-center">
                        <span
                          className="inline-block px-3 py-1 rounded-full text-xs font-bold"
                          style={{ background: NIVEL_BG[cell.nivel] || "#f1f5f9", color: NIVEL_COLORS[cell.nivel] || "#94a3b8" }}
                        >
                          {cell.label}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Diff LLM clásico */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <DiffCard title="Qué mejoró" icon={<TrendingUp className="w-4 h-4" />} color="#0F6E56" bg="#ecfdf5" items={diff.mejoro} testid="consulting-compare-mejoro" />
          <DiffCard title="Qué empeoró" icon={<TrendingDown className="w-4 h-4" />} color="#dc2626" bg="#fef2f2" items={diff.empeoro} testid="consulting-compare-empeoro" />
          <DiffCard title="Riesgos nuevos" icon={<AlertTriangle className="w-4 h-4" />} color="#c2410c" bg="#fff7ed" items={diff.riesgos_nuevos} testid="consulting-compare-riesgos" />
        </div>

        {diff.conclusion && (
          <div className="bg-white rounded-2xl shadow p-6 mb-6" data-testid="consulting-compare-conclusion">
            <div className="flex items-start gap-3">
              <MessageSquare className="w-5 h-5 mt-0.5" style={{ color: "#0F6E56" }} />
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Lectura estratégica</div>
                <p className="text-slate-700 leading-relaxed">{renderRich(diff.conclusion)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DiffCard({ title, icon, color, bg, items = [], testid }) {
  return (
    <div className="rounded-2xl p-5 border" style={{ background: bg, borderColor: `${color}33` }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3" style={{ color }}>
        {icon}
        <div className="text-xs uppercase tracking-widest font-bold">{title}</div>
      </div>
      {items.length ? (
        <ul className="space-y-2 text-sm">
          {items.map((t, i) => (
            <li key={i} className="text-slate-700 leading-snug">• {renderRich(t)}</li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-slate-400 italic">Sin datos suficientes.</p>
      )}
    </div>
  );
}

function TopChangesCard({ title, rows, icon, color, bg, testid }) {
  return (
    <div className="rounded-2xl p-5 border" style={{ background: bg, borderColor: `${color}33` }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3" style={{ color }}>
        {icon}
        <div className="text-xs uppercase tracking-widest font-bold">{title}</div>
      </div>
      {rows.length ? (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.code} className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold flex-shrink-0" style={{ background: color, color: "#fff" }}>
                {r.code}
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 text-sm truncate">{r.name}</div>
                <div className="text-xs text-slate-600 flex items-center gap-1.5 flex-wrap">
                  <span className="inline-block px-2 py-0.5 rounded-full" style={{ background: NIVEL_BG[r.from], color: NIVEL_COLORS[r.from] }}>{r.from_label}</span>
                  <ArrowRight className="w-3 h-3 text-slate-400" />
                  <span className="inline-block px-2 py-0.5 rounded-full" style={{ background: NIVEL_BG[r.to], color: NIVEL_COLORS[r.to] }}>{r.to_label}</span>
                  <span className="text-slate-400 ml-1">({r.delta > 0 ? "+" : ""}{r.delta})</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-slate-400 italic">Sin variaciones destacadas.</p>
      )}
    </div>
  );
}

function TimelineSVG({ points }) {
  // ancho responsivo; altura fija cómoda
  const W = 720;
  const H = 200;
  const PAD_L = 44;
  const PAD_R = 24;
  const PAD_T = 24;
  const PAD_B = 44;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  // eje X: distribuir por índice cronológico
  const n = points.length;
  const xAt = (i) => PAD_L + (n === 1 ? innerW / 2 : (innerW * i) / (n - 1));
  // eje Y: madurez 0-100 → invertido
  const yAt = (v) => PAD_T + innerH - (Math.max(0, Math.min(100, v)) / 100) * innerH;
  // valor a mostrar/plotear: preferimos `pct` (0-100); si no hay, derivamos de `avg` (1-4)
  const valOf = (p) => (typeof p.pct === "number" ? p.pct : Math.round(((p.avg - 1) / 3) * 100));

  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(1)} ${yAt(valOf(p)).toFixed(1)}`)
    .join(" ");

  // gridlines horizontales cada 25 puntos
  const gridLevels = [0, 25, 50, 75, 100];

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet" className="min-w-[520px]">
        {/* Fondo */}
        <rect x={PAD_L} y={PAD_T} width={innerW} height={innerH} fill="#fafaf7" rx={12} />

        {/* Gridlines */}
        {gridLevels.map((lvl) => (
          <g key={lvl}>
            <line
              x1={PAD_L}
              x2={PAD_L + innerW}
              y1={yAt(lvl)}
              y2={yAt(lvl)}
              stroke="#e2e8f0"
              strokeDasharray="3 4"
              strokeWidth={0.8}
            />
            <text
              x={PAD_L - 8}
              y={yAt(lvl) + 4}
              textAnchor="end"
              fontSize="10"
              fill="#94a3b8"
              fontWeight="600"
            >
              {lvl}
            </text>
          </g>
        ))}

        {/* Línea entre puntos con gradiente */}
        <defs>
          <linearGradient id="tl-line" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#0F6E56" />
            <stop offset="100%" stopColor="#C9A84C" />
          </linearGradient>
          <linearGradient id="tl-area" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0F6E56" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#0F6E56" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Área debajo de la línea */}
        {points.length > 1 && (
          <path
            d={`${pathD} L ${xAt(n - 1).toFixed(1)} ${(PAD_T + innerH).toFixed(1)} L ${xAt(0).toFixed(1)} ${(PAD_T + innerH).toFixed(1)} Z`}
            fill="url(#tl-area)"
          />
        )}

        {points.length > 1 && (
          <path d={pathD} fill="none" stroke="url(#tl-line)" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
        )}

        {/* Puntos + etiquetas */}
        {points.map((p, i) => {
          const v = valOf(p);
          const cx = xAt(i);
          const cy = yAt(v);
          const dateShort = p.date ? new Date(p.date).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }) : "";
          return (
            <g key={p.id}>
              <circle cx={cx} cy={cy} r={6.5} fill="#fff" stroke="#0F6E56" strokeWidth={2.5} />
              <circle cx={cx} cy={cy} r={2.8} fill="#C9A84C" />
              {/* pct encima del punto */}
              <text x={cx} y={cy - 12} textAnchor="middle" fontSize="11" fontWeight="700" fill="#0b3d2e">
                {v}
              </text>
              {/* label + fecha debajo */}
              <text x={cx} y={PAD_T + innerH + 18} textAnchor="middle" fontSize="10" fontWeight="600" fill="#334155">
                {truncate(p.label, 14)}
              </text>
              {dateShort && (
                <text x={cx} y={PAD_T + innerH + 32} textAnchor="middle" fontSize="9" fill="#94a3b8">
                  {dateShort}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function truncate(str, n) {
  if (!str) return "";
  return str.length > n ? `${str.slice(0, n - 1)}…` : str;
}

/**
 * Divide un párrafo largo en 2-3 párrafos más cortos para mejorar legibilidad.
 * Si el texto ya tiene saltos dobles, los respeta. Si no, agrupa oraciones
 * (2 o 3 por bloque) intentando repartirlas equilibradamente.
 */
function splitIntoParagraphs(text) {
  if (!text) return [];
  const trimmed = text.trim();
  // Si ya viene con dobles saltos, respetarlos
  if (trimmed.includes("\n\n")) {
    return trimmed.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  }
  // Detectar oraciones: punto (o interrogación/exclamación) + espacio + mayúscula
  const sentences = trimmed
    .split(/(?<=[.!?])\s+(?=[A-ZÁÉÍÓÚÑ¿¡])/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (sentences.length <= 2) return [trimmed];
  // Reparte en 2-3 párrafos según cantidad de oraciones
  const nParas = sentences.length >= 6 ? 3 : 2;
  const perPara = Math.ceil(sentences.length / nParas);
  const out = [];
  for (let i = 0; i < sentences.length; i += perPara) {
    out.push(sentences.slice(i, i + perPara).join(" "));
  }
  return out;
}

function RadarSVG({ summaries, areas, colors }) {
  const SIZE = 320;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  const R = 120;
  const N = areas.length;
  const angle = (i) => (-Math.PI / 2) + (2 * Math.PI * i) / N;

  const gridLevels = [1, 2, 3, 4];
  const point = (i, level) => {
    const rr = (level / 4) * R;
    return [CX + rr * Math.cos(angle(i)), CY + rr * Math.sin(angle(i))];
  };

  return (
    <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
      {/* Grid concentric */}
      {gridLevels.map((lvl) => {
        const pts = areas.map((_, i) => point(i, lvl).join(",")).join(" ");
        return <polygon key={lvl} points={pts} fill="none" stroke="#e2e8f0" strokeWidth={0.8} />;
      })}
      {/* Axes */}
      {areas.map((a, i) => {
        const [x, y] = point(i, 4);
        return <line key={a.code} x1={CX} y1={CY} x2={x} y2={y} stroke="#e2e8f0" strokeWidth={0.5} />;
      })}
      {/* Series */}
      {summaries.map((s, idx) => {
        const color = colors[idx] || "#0F6E56";
        const pts = areas.map((a, i) => {
          const nivel = s.per_area?.[a.code]?.nivel || 0;
          return point(i, Math.max(0.15, nivel)).join(",");
        }).join(" ");
        return (
          <g key={s.id}>
            <polygon points={pts} fill={color} fillOpacity="0.12" stroke={color} strokeWidth={2} />
            {areas.map((a, i) => {
              const nivel = s.per_area?.[a.code]?.nivel || 0;
              const [x, y] = point(i, Math.max(0.15, nivel));
              return <circle key={a.code} cx={x} cy={y} r={3.2} fill={color} />;
            })}
          </g>
        );
      })}
      {/* Labels */}
      {areas.map((a, i) => {
        const [x, y] = point(i, 4);
        const dx = x - CX;
        const dy = y - CY;
        const lx = CX + dx * 1.13;
        const ly = CY + dy * 1.13;
        return (
          <text
            key={a.code}
            x={lx}
            y={ly}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="11"
            fontWeight="bold"
            fill="#0F6E56"
          >
            {a.code}
          </text>
        );
      })}
    </svg>
  );
}
